import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { creerClient, type Client } from '../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton } from '../../app/styles.js';

/**
 * Couloir G, point 6 : dépôt réel d'un vocal à l'étape 1 (entretien d'accueil). Le fichier audio (ou l'enregistrement au micro,
 * quand le navigateur le permet) est envoyé au dossier par morceaux (`accueil.vocaux.ouvrir`, `.morceau`, `.terminer`). Sans
 * service de transcription configuré, il reste déposé « transcription en attente », et l'écran le dit. La transcription, quand elle
 * existe, est toujours « à vérifier ».
 */
export const TAILLE_MORCEAU = 512 * 1024;
const TAILLE_MAX = 25 * 1024 * 1024;

export interface VocalLu {
  id: string;
  nomFichier: string;
  mime: string;
  taille: number;
  statut: 'televersement' | 'transcription_en_attente' | 'transcrit' | 'echec_transcription';
  transcription: string | null;
  deposePar: string;
  deposeLe: string;
}

export const LIBELLE_STATUT: Readonly<Record<VocalLu['statut'], string>> = {
  televersement: 'Dépôt interrompu : déposez-le de nouveau.',
  transcription_en_attente: 'Transcription en attente : aucun service de transcription n’est encore branché. Le vocal est gardé au dossier.',
  transcrit: 'Transcrit. Texte à vérifier avant de s’en servir.',
  echec_transcription: 'La transcription a échoué. Le vocal est gardé au dossier ; la transcription sera relancée.',
};

/** Statut court, dit dans la phrase de retour et sur chaque ligne de la liste (tournage du 30/10, défaut 3). */
export const STATUT_COURT: Readonly<Record<VocalLu['statut'], string>> = {
  televersement: 'dépôt interrompu, déposez-le de nouveau',
  transcription_en_attente: 'transcription en attente',
  transcrit: 'transcrit, texte à vérifier',
  echec_transcription: 'transcription en échec, elle sera relancée',
};

const FORMAT_GUADELOUPE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'America/Guadeloupe',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** « le 30/10 à 10:05 », en heure de Guadeloupe (celle des utilisatrices), quel que soit le fuseau de l'appareil. */
export function quandDepose(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = Object.fromEntries(FORMAT_GUADELOUPE.formatToParts(d).map((x) => [x.type, x.value]));
  return `le ${p['day']}/${p['month']} à ${p['hour']}:${p['minute']}`;
}

const enBase64 = (octets: Uint8Array): string => {
  let binaire = '';
  for (let i = 0; i < octets.length; i += 0x8000) binaire += String.fromCharCode(...octets.subarray(i, i + 0x8000));
  return btoa(binaire);
};

/** Dépose un fichier audio au dossier, morceau par morceau ; rend le vocal tel que le serveur l'a gardé. */
export async function deposerVocal(client: Pick<Client, 'appeler'>, dossierId: string, nomFichier: string, mime: string, octets: Uint8Array): Promise<VocalLu> {
  const parametres = { dossierId };
  const v = await client.appeler<VocalLu>('accueil.vocaux.ouvrir', { parametres, corps: { nomFichier, mime, taille: octets.length } });
  const p = { dossierId, vocalId: v.id };
  for (let i = 0, index = 0; i < octets.length; i += TAILLE_MORCEAU, index += 1)
    await client.appeler('accueil.vocaux.morceau', { parametres: p, corps: { index, donnees: enBase64(octets.subarray(i, i + TAILLE_MORCEAU)) } });
  return client.appeler<VocalLu>('accueil.vocaux.terminer', { parametres: p, corps: {} });
}

const bouton: CSSProperties = { ...styleBouton, minHeight: 48, padding: '0 20px', fontSize: 16 };
const micro = (): boolean => typeof globalThis.MediaRecorder === 'function' && typeof globalThis.navigator?.mediaDevices?.getUserMedia === 'function';

export function DepotVocal({ dossierId, client }: { dossierId: string | null; client?: Pick<Client, 'appeler'> }) {
  const c = useMemo(() => client ?? creerClient({ fetch: (...a) => globalThis.fetch(...a) }), [client]);
  const [vocaux, setVocaux] = useState<VocalLu[]>([]);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<{ texte: string; detail?: string } | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  const enregistreur = useRef<MediaRecorder | null>(null);

  useEffect(() => {
    if (!dossierId) return;
    let annule = false;
    c.appeler<VocalLu[]>('accueil.vocaux.lister', { parametres: { dossierId } }).then(
      (l) => !annule && setVocaux(l),
      () => undefined,
    );
    return () => {
      annule = true;
    };
  }, [c, dossierId]);

  if (!dossierId)
    return (
      <p role="status" data-depot-vocal="" style={{ marginBottom: 16, color: COULEURS.ardoise }}>
        Le vocal se range dans le dossier : enregistrez d’abord l’entretien (le dossier est alors créé), puis déposez le vocal. Cabinet MAAT
      </p>
    );

  const envoyer = async (nom: string, mime: string, octets: Uint8Array) => {
    if (octets.length === 0) return setMessage({ texte: 'Ce fichier est vide.' });
    if (octets.length > TAILLE_MAX) return setMessage({ texte: 'Ce vocal dépasse 25 Mo : coupez-le en plusieurs parties.' });
    setEnCours(true);
    setMessage({ texte: 'Dépôt du vocal en cours…' });
    try {
      const v = await deposerVocal(c, dossierId, nom, mime, octets);
      setVocaux((l) => [v, ...l.filter((x) => x.id !== v.id)]);
      setMessage({ texte: `Vocal déposé au dossier ${quandDepose(v.deposeLe)} : ${STATUT_COURT[v.statut]}.`, detail: LIBELLE_STATUT[v.statut] });
    } catch {
      setMessage({ texte: 'Le vocal n’a pas été déposé. Vérifiez la connexion puis réessayez.' });
    } finally {
      setEnCours(false);
    }
  };

  const basculerMicro = async () => {
    if (enregistreur.current) {
      enregistreur.current.stop();
      return;
    }
    try {
      const flux = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(flux);
      const morceaux: Blob[] = [];
      r.ondataavailable = (e) => morceaux.push(e.data);
      r.onstop = () => {
        flux.getTracks().forEach((t) => t.stop());
        enregistreur.current = null;
        setEnregistre(false);
        const blob = new Blob(morceaux, { type: r.mimeType || 'audio/webm' });
        void blob.arrayBuffer().then((b) => envoyer(`entretien-${new Date().toISOString().slice(0, 16).replace(':', 'h')}.webm`, blob.type, new Uint8Array(b)));
      };
      enregistreur.current = r;
      r.start();
      setEnregistre(true);
    } catch {
      setMessage({ texte: 'Le micro n’est pas accessible : autorisez-le, ou déposez un fichier audio.' });
    }
  };

  return (
    <div data-depot-vocal="" style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label htmlFor="accueil-vocal-depot" style={{ display: 'block', color: COULEURS.marine }}>
        Déposer le vocal de l’entretien (fichier audio)
      </label>
      <input
        id="accueil-vocal-depot"
        type="file"
        accept="audio/*"
        disabled={enCours || enregistre}
        style={{ minHeight: CIBLE_MIN, font: 'inherit' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void f.arrayBuffer().then((b) => envoyer(f.name || 'vocal', f.type || 'audio/mp4', new Uint8Array(b)));
        }}
      />
      {message ? (
        // Juste sous le champ, pour être vu sans défiler (tournage du 30/10, défaut 3).
        <div role="status" data-retour-vocal="" style={{ border: `1px solid ${COULEURS.bordure}`, borderRadius: 12, padding: '10px 14px', background: COULEURS.blanc }}>
          <p style={{ margin: 0, fontWeight: 600, color: COULEURS.marine }}>{message.texte}</p>
          {message.detail ? <p style={{ margin: '4px 0 0', color: COULEURS.ardoise, fontSize: 15 }}>{message.detail}</p> : null}
        </div>
      ) : null}
      {micro() ? (
        <div>
          <button type="button" style={bouton} disabled={enCours} onClick={() => void basculerMicro()}>
            {enregistre ? 'Arrêter et déposer l’enregistrement' : 'Enregistrer au micro'}
          </button>
        </div>
      ) : null}
      {vocaux.length ? <p style={{ margin: 0, fontWeight: 600, color: COULEURS.marine }}>Vocaux déposés pour ce dossier ({vocaux.length})</p> : null}
      {vocaux.length ? (
        <ul aria-label="Vocaux du dossier" style={{ margin: 0, paddingLeft: 20 }}>
          {vocaux.map((v) => (
            <li key={v.id} data-vocal={v.statut}>
              {v.nomFichier}, déposé {quandDepose(v.deposeLe)} par {v.deposePar} : {STATUT_COURT[v.statut]}
              {v.transcription ? <p style={{ margin: '4px 0', fontStyle: 'italic' }}>« {v.transcription} »</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
